/**
 * Canvas 模板渲染器模块
 * 
 * 本模块提供了基于 Fabric.js 的模板渲染功能，支持将模板配置和数据绑定后渲染为图像。
 * 主要功能包括：
 * - 单个模板渲染
 * - 批量模板渲染
 * - 数据绑定（文本、条形码、二维码）
 * - 支持自定义渲染选项（格式、质量、缩放倍数）
 */

import * as fabric from 'fabric';
import { UnitConverter } from './unit-converter';
import { generateBarcode } from './barcode-generator';
import { configManager } from '../config';
import type { TemplateConfig, ProgressCallback, RenderOptions } from './types';

/**
 * 调试日志开关
 * 设置为 true 时显示详细的批量渲染调试日志
 * 设置为 false 时关闭调试日志
 */
const DEBUG_BATCH_RENDER = false;

/**
 * 获取默认渲染选项配置
 * 从配置管理器获取全局渲染质量设置
 */
function getDefaultRenderOptions(): Required<RenderOptions> {
    const renderConfig = configManager.getRenderConfig();
    return {
        format: renderConfig.format,
        quality: renderConfig.quality,
        multiplier: renderConfig.multiplier,
        enableAntiAliasing: renderConfig.enableAntiAliasing,
        enableImageSmoothing: renderConfig.enableImageSmoothing,
        colorDepth: renderConfig.colorDepth,
        colorMode: renderConfig.colorMode || 'color',
        applyImageProcessing: true
    };
}

/**
 * 模板渲染器类
 *
 * 负责将模板配置和数据渲染成图像。支持单个渲染和批量渲染两种模式。
 */
/**
 * 模板渲染器类
 *
 * 负责将模板配置和数据渲染成图像，支持单个和批量渲染。
 * 使用 Fabric.js 进行画布操作，提供高质量的图像输出。
 *
 * 主要特性：
 * - 支持多种输出格式（PNG、JPEG、WebP）
 * - 可配置渲染质量和色彩模式
 * - 批量渲染优化（复用画布实例）
 * - 自动数据绑定（文本、条形码、二维码）
 * - 图像处理（色彩转换、压缩）
 */
export class TemplateRenderer {
    /** Fabric.js 画布实例，用于对象操作和渲染 */
    private canvas: any = null;

    /** HTML Canvas 元素，用于最终图像输出 */
    private canvasEl: HTMLCanvasElement | null = null;

    /** 标记当前画布是否来自外部调用（用于批量渲染优化） */
    private isExternalCanvas = false;

    /** 渲染操作的超时时间（毫秒），防止长时间卡死 */
    private renderTimeout = 30000;

    /**
     * 渲染单个模板
     *
     * 根据模板配置和数据绑定生成图像。处理流程：
     * 1. 计算画布尺寸（基于模板尺寸和DPI）
     * 2. 创建Fabric.js画布实例
     * 3. 加载模板对象到画布
     * 4. 执行数据绑定（文本、条形码、二维码）
     * 5. 应用渲染质量设置
     * 6. 渲染并导出为二进制图像数据
     *
     * @param template - 模板配置对象，包含尺寸、DPI和对象列表
     * @param data - 数据对象，键名对应模板组件的componentName
     * @param options - 可选的渲染选项，用于覆盖默认设置
     * @returns Promise<Uint8Array> - 返回图像的二进制数据
     */
    async renderTemplate(
        template: TemplateConfig,
        data: Record<string, any>,
        options?: RenderOptions
    ): Promise<Uint8Array> {
        // 获取默认选项并合并用户选项
        const defaultOpts = getDefaultRenderOptions();
        const opts = { ...defaultOpts, ...options };

        // 根据模板尺寸和 DPI 计算 canvas 尺寸
        const size = UnitConverter.calculateCanvasSize(template.width, template.height, template.dpi);

        // 应用最大渲染尺寸限制
        const renderConfig = configManager.getRenderConfig();
        const maxSize = renderConfig.maxRenderSize || 4096;
        const limitedSize = {
            width: Math.min(size.width, maxSize),
            height: Math.min(size.height, maxSize)
        };

        // 如果尺寸被限制，进行等比例缩放
        if (limitedSize.width !== size.width || limitedSize.height !== size.height) {
            const scale = Math.min(limitedSize.width / size.width, limitedSize.height / size.height);
            limitedSize.width = Math.floor(size.width * scale);
            limitedSize.height = Math.floor(size.height * scale);
        }

        // 创建新的 canvas 元素
        this.canvasEl = document.createElement('canvas');
        this.canvasEl.width = limitedSize.width;
        this.canvasEl.height = limitedSize.height;

        // 初始化 Fabric.js canvas
        this.canvas = new (fabric as any).Canvas(this.canvasEl, {
            width: size.width,
            height: size.height,
            backgroundColor: template.background || '#ffffff',
            selection: false,              // 禁用选择功能
            renderOnAddRemove: false       // 禁用自动渲染
        });
        this.isExternalCanvas = false;

        // 应用渲染质量控制设置
        this.applyRenderQualitySettings(opts);

        // 准备 canvas 数据
        const canvasData: any = {
            version: '5.3.0',
            objects: template.objects || []
        };
        if (template.background) canvasData.background = template.background;
        if (template.backgroundImage) canvasData.backgroundImage = template.backgroundImage;

        // 加载模板数据到 canvas
        await this.canvas.loadFromJSON(canvasData);

        // 为文本对象设置缩放限制属性和默认自动换行
        const objects = this.canvas.getObjects();
        objects.forEach((obj: any) => {
            if (obj.type === 'textbox' || obj.type === 'text') {
                obj.set({
                    lockScalingFlip: true,
                    lockScalingY: true,
                    splitByGrapheme: true
                });
            }
        });

        // 绑定数据到模板对象
        await this.bindData(data, template.objects);

        // 渲染 canvas
        this.canvas.renderAll();

        // 将 canvas 转换为 Data URL
        const globalRenderConfig = configManager.getRenderConfig();

        // 检查 canvas 元素是否存在
        if (!this.canvasEl) {
            throw new Error('Canvas element is not initialized');
        }

        // 应用色彩模式转换
        const colorMode = opts.colorMode || globalRenderConfig.colorMode || 'color';
        const finalCanvas = this.applyColorMode(this.canvasEl, colorMode);

        // 确保最终的 canvas 不为 null
        if (!finalCanvas) {
            throw new Error('Failed to process canvas for rendering');
        }

        const format = opts.format || globalRenderConfig.format;
        const quality = opts.quality || globalRenderConfig.quality;

        // 使用二进制数据格式，减少传输开销
        let mimeType: string;
        if (format === 'jpeg') {
            mimeType = 'image/jpeg';
        } else {
            mimeType = format === 'webp' ? 'image/webp' : 'image/png';
        }

        // 获取canvas的二进制blob数据
        const blob = await new Promise<Blob>((resolve, reject) => {
            finalCanvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to convert canvas to blob'));
                }
            }, mimeType, format === 'jpeg' ? quality : undefined);
        });

        // 转换为Uint8Array二进制数组
        const arrayBuffer = await blob.arrayBuffer();
        const binaryData = new Uint8Array(arrayBuffer);

        // 清理资源
        this.dispose();

        // 返回二进制数据而不是base64
        return binaryData;
    }

    /**
     * 批量渲染模板
     *
     * 高效地批量渲染多组数据为图像。支持两种渲染模式：
     * 1. 标准模式：每次创建新画布，适合少量数据
     * 2. 外部画布模式：复用画布实例，适合大量数据（性能优化）
     *
     * 渲染流程：
     * - 恢复画布到原始模板状态
     * - 遍历每组数据并绑定到组件
     * - 渲染并导出图像
     * - 报告进度给调用者
     *
     * @param template - 模板配置对象
     * @param dataArray - 数据数组，每个元素是一组数据对应一张图像
     * @param onProgress - 可选的进度回调函数，参数：(当前索引, 总数)
     * @param externalCanvas - 可选的外部Fabric.js画布实例，用于性能优化
     * @returns Promise<Uint8Array[]> - 返回图像二进制数据数组
     */
    async renderBatch(
        template: TemplateConfig,
        dataArray: Array<Record<string, any>>,
        onProgress?: ProgressCallback,
        externalCanvas?: any
    ): Promise<Uint8Array[]> {
        const images: Uint8Array[] = [];

        if (externalCanvas) {
            // 使用外部 canvas 进行批量渲染（性能优化模式）
            this.canvas = externalCanvas;
            this.isExternalCanvas = true;

            // 清除选中状态，避免上层控件影响导出
            if (this.canvas && typeof this.canvas.discardActiveObject === 'function') {
                this.canvas.discardActiveObject();
            }

            // 临时隐藏所有编辑态的辅助对象（不参与打印）
            const uiOverlayObjects: any[] = this.canvas.getObjects().filter((obj: any) =>
                obj.isBorderOverlay || obj.isGuideline || obj.isResizeGuideline
            );
            const overlayVisibility = uiOverlayObjects.map((obj: any) => ({ obj, visible: obj.visible }));
            uiOverlayObjects.forEach((obj: any) => obj.set('visible', false));
            if (this.canvas && typeof this.canvas.requestRenderAll === 'function') {
                this.canvas.requestRenderAll();
            }

            for (let i = 0; i < dataArray.length; i++) {
                if (DEBUG_BATCH_RENDER) {
                    console.log(`\n=== [批量渲染] 第 ${i + 1}/${dataArray.length} 次渲染 ===`);
                    console.log('[批量渲染] 当前数据:', dataArray[i]);
                }

                // 检查是否被取消（通过progress回调抛出错误）
                if (onProgress) {
                    onProgress(i + 1, dataArray.length);
                }

                // 绑定当前数据
                await this.bindData(dataArray[i], template.objects);
                this.canvas.renderAll();

                // 导出为图像
                const renderConfig = configManager.getRenderConfig();
                const colorMode = renderConfig.colorMode || 'color';

                // 在外部canvas模式下，使用二进制数据格式
                let binaryData: Uint8Array;

                if (colorMode === 'color') {
                    // 无需色彩转换，直接使用Fabric.js canvas
                    const htmlCanvas = this.canvas.lowerCanvasEl || this.canvasEl;
                    if (!htmlCanvas) {
                        throw new Error('Canvas element is not available for binary export in batch rendering');
                    }

                    const mimeType = renderConfig.format === 'jpeg' ? 'image/jpeg' :
                                   renderConfig.format === 'webp' ? 'image/webp' : 'image/png';

                    const blob = await new Promise<Blob>((resolve, reject) => {
                        htmlCanvas.toBlob((blob: Blob | null) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error('Failed to convert canvas to blob'));
                            }
                        }, mimeType, renderConfig.format === 'jpeg' ? renderConfig.quality : undefined);
                    });

                    const arrayBuffer = await blob.arrayBuffer();
                    binaryData = new Uint8Array(arrayBuffer);
                } else {
                    // 需要色彩转换，从Fabric.js canvas获取HTML canvas元素
                    const htmlCanvas = this.canvas.lowerCanvasEl || this.canvasEl;

                    if (!htmlCanvas) {
                        throw new Error('Canvas element is not available for color processing in batch rendering');
                    }

                    const finalCanvas = this.applyColorMode(htmlCanvas, colorMode);

                    if (!finalCanvas) {
                        throw new Error('Failed to process canvas for batch rendering');
                    }

                    const mimeType = renderConfig.format === 'jpeg' ? 'image/jpeg' :
                                   renderConfig.format === 'webp' ? 'image/webp' : 'image/png';

                    const blob = await new Promise<Blob>((resolve, reject) => {
                        finalCanvas.toBlob((blob) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error('Failed to convert canvas to blob'));
                            }
                        }, mimeType, renderConfig.format === 'jpeg' ? renderConfig.quality : undefined);
                    });

                    const arrayBuffer = await blob.arrayBuffer();
                    binaryData = new Uint8Array(arrayBuffer);
                }

                images.push(binaryData);
                if (DEBUG_BATCH_RENDER) {
                    console.log(`[批量渲染] 第 ${i + 1} 次渲染完成\n`);
                }

                // 触发进度回调
                if (onProgress) {
                    onProgress(i + 1, dataArray.length);
                }
            }

            // 恢复辅助对象的可见性
            try {
                overlayVisibility.forEach(({ obj, visible }) => {
                    if (obj && typeof obj.set === 'function') {
                        obj.set('visible', visible);
                    }
                });
                if (this.canvas && typeof this.canvas.requestRenderAll === 'function') {
                    this.canvas.requestRenderAll();
                }
            } catch {}

            // 清空 canvas 引用（不销毁外部 canvas）
            this.canvas = null;
            this.isExternalCanvas = false;
        } else {
            // 使用标准渲染模式（每次创建新 canvas）
            for (let i = 0; i < dataArray.length; i++) {
                const imageData: Uint8Array = await this.renderTemplate(template, dataArray[i]);
                images.push(imageData);
                if (onProgress) {
                    onProgress(i + 1, dataArray.length);
                }
            }
        }

        return images;
    }

    /**
     * 将数据绑定到模板对象
     *
     * 核心数据绑定逻辑，支持多种组件类型：
     * - 文本组件：设置文本内容，自动调整字体大小
     * - 条形码/二维码：生成对应的条码图像
     * - 图片组件：根据名称推断是否为条码类型
     *
     * 处理逻辑：
     * 1. 恢复画布对象到原始模板状态（批量渲染时）
     * 2. 遍历所有组件，根据componentName匹配数据
     * 3. 根据组件类型执行相应的数据绑定
     * 4. 处理可见性和渲染控制（disablePrintRender等）
     * 5. 应用自动缩小字体功能
     *
     * @param data - 数据对象，键为组件名称，值为要绑定的数据
     * @param templateObjects - 原始模板对象数组，用于状态恢复
     */
    private async bindData(data: Record<string, any>, templateObjects: any[]): Promise<void> {
        // 过滤掉线框组件和辅助线，并展开 Group 对象
        const allObjects = this.canvas.getObjects().filter((obj: any) => !obj.isBorderOverlay && !obj.isGuideline && !obj.isResizeGuideline);
        const objects: any[] = [];
        
        if (DEBUG_BATCH_RENDER) {
            console.log('[bindData] 画布对象数量（过滤后）:', allObjects.length);
        }
        
        allObjects.forEach((obj: any, index: number) => {
            if (obj.type === 'group') {
                const groupObjects = obj.getObjects();
                if (DEBUG_BATCH_RENDER) {
                    console.log(`[bindData] 发现 Group 对象 [${index}]:`, {
                        componentName: obj.componentName,
                        groupObjectsCount: groupObjects.length
                    });
                    groupObjects.forEach((gObj: any, gIndex: number) => {
                        console.log(`  - Group 内对象 [${gIndex}]:`, {
                            type: gObj.type,
                            componentName: gObj.componentName,
                            id: gObj.id
                        });
                    });
                }
                objects.push(...groupObjects);
            } else {
                if (DEBUG_BATCH_RENDER) {
                    console.log(`[bindData] 普通对象 [${index}]:`, {
                        type: obj.type,
                        componentName: obj.componentName,
                        id: obj.id
                    });
                }
                objects.push(obj);
            }
        });
        
        if (DEBUG_BATCH_RENDER) {
            console.log('[bindData] 展开后对象总数:', objects.length);
            console.log('[bindData] 模板对象总数:', templateObjects?.length || 0);
        }

        // 批量渲染优化：恢复画布对象到原始模板状态
        // 避免每次渲染都重新加载模板，提高性能
        if (this.isExternalCanvas && templateObjects) {
            if (DEBUG_BATCH_RENDER) {
                console.log('[恢复阶段] 开始恢复，对象总数:', objects.length);
            }
            
            // 创建模板对象的映射表（按 id 和 componentName）
            const templateObjMap = new Map<string, any>();
            templateObjects.forEach((tObj: any) => {
                if (tObj.id) {
                    templateObjMap.set(tObj.id, tObj);
                }
                if (tObj.componentName) {
                    templateObjMap.set(tObj.componentName, tObj);
                }
            });
            
            for (let i = 0; i < objects.length; i++) {
                const obj = objects[i];
                // 优先用 id 匹配，其次用 componentName
                let templateObj = obj.id ? templateObjMap.get(obj.id) : null;
                if (!templateObj && obj.componentName) {
                    templateObj = templateObjMap.get(obj.componentName);
                }

                if (!templateObj) {
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[恢复阶段] 对象未找到模板:', {
                            id: obj.id,
                            componentName: obj.componentName
                        });
                    }
                    continue;
                }

                if (DEBUG_BATCH_RENDER) {
                    console.log('[恢复阶段] 处理对象:', {
                        index: i,
                        componentName: obj.componentName,
                        type: obj.type,
                        hasObjBarcodeData: !!obj.barcodeData,
                        hasObjQrData: !!obj.qrData,
                        hasTemplateBarcodeData: !!templateObj.barcodeData,
                        hasTemplateQrData: !!templateObj.qrData,
                        templateBarcodeDataType: templateObj.barcodeData?.type,
                        templateQrDataValue: templateObj.qrData?.value
                    });
                }

                if (obj.type === 'textbox' || obj.type === 'text') {
                    // 恢复原始文本内容
                    obj.set('text', templateObj.text || '');
                    // 恢复原始字体大小
                    if (templateObj.fontSize) {
                        obj.set('fontSize', templateObj.fontSize);
                    }
                    // 重置可见性
                    obj.set('visible', true);
                    // 重新计算文本尺寸，确保自动换行生效
                    obj.initDimensions();
                    obj.setCoords();
                } else if (templateObj.barcodeData) {
                    // 恢复条形码数据结构（只要template有barcodeData就恢复，不管obj是否有）
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[恢复阶段] 恢复条码组件:', {
                            componentName: obj.componentName,
                            type: templateObj.barcodeData.type,
                            originalValue: templateObj.barcodeData.value
                        });
                    }
                    // 确保obj有barcodeData对象
                    if (!obj.barcodeData) {
                        obj.barcodeData = {};
                    }
                    obj.barcodeData.value = templateObj.barcodeData.value || '';
                    obj.barcodeData.type = templateObj.barcodeData.type;
                    if (templateObj.barcodeData.height) {
                        obj.barcodeData.height = templateObj.barcodeData.height;
                    }
                    if (templateObj.barcodeData.qrOptions) {
                        obj.barcodeData.qrOptions = templateObj.barcodeData.qrOptions;
                    }
                    obj.set('visible', true);
                    // 注意：不在这里生成条码图像，将在后续的数据绑定阶段根据实际数据生成
                } else if (templateObj.qrData) {
                    // 恢复二维码数据结构（只要template有qrData就恢复，不管obj是否有）
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[恢复阶段] 恢复二维码组件:', {
                            componentName: obj.componentName,
                            originalValue: templateObj.qrData.value
                        });
                    }
                    // 确保obj有qrData对象
                    if (!obj.qrData) {
                        obj.qrData = {};
                    }
                    obj.qrData.value = templateObj.qrData.value || '';
                    obj.set('visible', true);
                    // 注意：不在这里生成二维码图像，将在后续的数据绑定阶段根据实际数据生成
                }
                // 恢复所有自定义属性
                if (templateObj.componentName) obj.componentName = templateObj.componentName;
                if (templateObj.id) obj.id = templateObj.id;
                if (templateObj.qrData) obj.qrData = templateObj.qrData;
                // 注意：不要在这里覆盖 barcodeData，因为已经在上面处理过了
                if (templateObj.autoShrinkEnabled !== undefined) obj.autoShrinkEnabled = templateObj.autoShrinkEnabled;
                if (templateObj.maxLines !== undefined) obj.maxLines = templateObj.maxLines;
                if (templateObj.originalFontSize !== undefined) obj.originalFontSize = templateObj.originalFontSize;
                if (templateObj.minFontSize !== undefined) obj.minFontSize = templateObj.minFontSize;
                if (templateObj.maxFontSize !== undefined) obj.maxFontSize = templateObj.maxFontSize;
                if (templateObj.splitByGrapheme !== undefined) obj.splitByGrapheme = templateObj.splitByGrapheme;
                if (templateObj.disableBatchBinding !== undefined) obj.disableBatchBinding = templateObj.disableBatchBinding;
                if (templateObj.disablePrintRender !== undefined) obj.disablePrintRender = templateObj.disablePrintRender;
                // 恢复字体大小
                if (templateObj.fontSize !== undefined) obj.set('fontSize', templateObj.fontSize);
            }
        }

        // 绑定数据 - 只修改有对应数据的对象
        // 创建模板对象的映射表（如果还没创建）
        let templateObjMap: Map<string, any> | null = null;
        if (templateObjects && !this.isExternalCanvas) {
            templateObjMap = new Map<string, any>();
            templateObjects.forEach((tObj: any) => {
                if (tObj.id) {
                    templateObjMap!.set(tObj.id, tObj);
                }
                if (tObj.componentName) {
                    templateObjMap!.set(tObj.componentName, tObj);
                }
            });
        }
        
        for (let i = 0; i < objects.length; i++) {
            const obj = objects[i];

            // 如果不是外部 canvas，需要从模板对象复制元数据
            if (!this.isExternalCanvas && templateObjMap) {
                // 优先用 id 匹配，其次用 componentName
                let templateObj = obj.id ? templateObjMap.get(obj.id) : null;
                if (!templateObj && obj.componentName) {
                    templateObj = templateObjMap.get(obj.componentName);
                }
                
                if (templateObj) {
                    if (templateObj.componentName) obj.componentName = templateObj.componentName;
                    if (templateObj.id) obj.id = templateObj.id;
                    if (templateObj.qrData) obj.qrData = templateObj.qrData;
                    if (templateObj.barcodeData) {
                        obj.barcodeData = templateObj.barcodeData;
                        // 确保条码数据结构完整
                        if (obj.barcodeData.type && !obj.barcodeData.value) {
                            obj.barcodeData.value = '';
                        }
                    }
                    // 恢复自动缩小相关属性
                    if (templateObj.autoShrinkEnabled !== undefined) obj.autoShrinkEnabled = templateObj.autoShrinkEnabled;
                    if (templateObj.maxLines !== undefined) obj.maxLines = templateObj.maxLines;
                    if (templateObj.originalFontSize !== undefined) obj.originalFontSize = templateObj.originalFontSize;
                    if (templateObj.minFontSize !== undefined) obj.minFontSize = templateObj.minFontSize;
                    if (templateObj.maxFontSize !== undefined) obj.maxFontSize = templateObj.maxFontSize;
                    if (templateObj.splitByGrapheme !== undefined) obj.splitByGrapheme = templateObj.splitByGrapheme;
                    if (templateObj.disableBatchBinding !== undefined) obj.disableBatchBinding = templateObj.disableBatchBinding;
                    if (templateObj.disablePrintRender !== undefined) obj.disablePrintRender = templateObj.disablePrintRender;
                    // 恢复字体大小
                    if (templateObj.fontSize !== undefined) obj.set('fontSize', templateObj.fontSize);

                    // 重置可见性
                    obj.set('visible', true);

                    // 如果是 image 类型的条码，恢复原始图像
                    if (obj.type === 'image' && obj.barcodeData && obj.barcodeData.value) {
                        const barcodeImg = await generateBarcode(
                            obj.barcodeData.type,
                            obj.barcodeData.value,
                            obj.barcodeData.height || 100,
                            obj.barcodeData.qrOptions
                        );
                        await obj.setSrc(barcodeImg);
                    }
                }
            }

            const componentName = obj.componentName;

            // 如果对象没有 componentName，跳过
            if (!componentName) continue;

            // 如果对象设置了禁止绑定数据，跳过（这些组件应该始终保持可见）
            if (obj.disableBatchBinding) {
                if (DEBUG_BATCH_RENDER) {
                    console.log('[数据绑定] 跳过禁止绑定的组件:', componentName);
                }
                continue;
            }

            // 处理打印渲染控制：某些组件可能在编辑时显示但打印时隐藏
            if (obj.disablePrintRender) {
                obj.set('visible', false);
                if (DEBUG_BATCH_RENDER) {
                    console.log('[数据绑定] 隐藏不渲染打印的组件:', componentName);
                }
                continue;
            }

            // 检查数据中是否有对应的值
            const hasValue = data[componentName] !== undefined && data[componentName] !== null;
            const value = hasValue ? String(data[componentName]) : '';

            if (DEBUG_BATCH_RENDER) {
                console.log('[数据绑定] 处理组件:', {
                    componentName,
                    type: obj.type,
                    hasValue,
                    value,
                    hasBarcodeData: !!obj.barcodeData,
                    hasQrData: !!obj.qrData,
                    allDataKeys: Object.keys(data)
                });
            }

            // 根据对象类型进行数据绑定
            if (obj.type === 'textbox' || obj.type === 'text') {
                // 文本对象：如果有值则设置文本，否则隐藏
                if (hasValue) {
                    obj.set('text', value);
                    obj.set('visible', true);
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 文本组件赋值成功:', componentName, '=', value);
                    }

                    // 只有在没有原始字体大小的情况下，才使用当前字体大小
                    // 这样可以避免覆盖已经恢复的字体大小
                    if (!obj.originalFontSize && !this.isExternalCanvas) {
                        const fontSize = obj.fontSize;
                        if (fontSize) {
                            obj.set('fontSize', fontSize);
                        }
                    }

                    // 重新计算文本尺寸，确保自动换行生效
                    obj.initDimensions();
                    obj.setCoords();
                } else {
                    // 没有值时，隐藏该组件
                    obj.set('visible', false);
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 文本组件无数据，隐藏:', componentName);
                    }
                }
            } else if (obj.barcodeData || (obj.type === 'image' && obj.barcodeData)) {
                // 条形码/二维码组件处理：动态生成条码图像
                // 只在有数据时生成，避免空白条码
                if (hasValue && value.trim() !== '') {
                    // 特殊处理二维码：使用专用二维码生成器
                    // 支持自定义选项如容错级别、边距等
                    if (obj.barcodeData.type === 'qr') {
                        if (DEBUG_BATCH_RENDER) {
                            console.log('[数据绑定] 开始生成二维码:', componentName, '=', value);
                            console.log('[数据绑定] qrData内容:', { type: 'qr', value: obj.barcodeData.value, qrOptions: obj.barcodeData.qrOptions });
                            console.log('[数据绑定] qrOptions:', obj.barcodeData.qrOptions);
                        }
                        // 保存当前的视觉尺寸（考虑缩放）
                        // setSrc会重置width/height，但我们需要保持视觉外观
                        const currentWidth = obj.width * obj.scaleX;
                        const currentHeight = obj.height * obj.scaleY;

                        // 生成二维码图像
                        const qrImg = await generateBarcode('qr', value, 100, obj.barcodeData.qrOptions);
                        await obj.setSrc(qrImg);

                        // 恢复视觉尺寸：调整缩放以保持原有的显示大小
                        if (obj.width && obj.height) {
                            obj.scaleX = currentWidth / obj.width;
                            obj.scaleY = currentHeight / obj.height;
                        }

                        obj.barcodeData.value = value;
                        obj.set('visible', true);
                        if (DEBUG_BATCH_RENDER) {
                            console.log('[数据绑定] 二维码生成成功:', componentName, '=', value);
                        }
                    } else {
                        // 处理普通条码
                        if (DEBUG_BATCH_RENDER) {
                            console.log('[数据绑定] 开始生成条码:', {
                                componentName,
                                value,
                                type: obj.barcodeData.type,
                                height: obj.barcodeData.height
                            });
                        }
                        const barcodeImg = await generateBarcode(
                            obj.barcodeData.type,
                            value,
                            obj.barcodeData.height || 100
                        );
                        await obj.setSrc(barcodeImg);
                        obj.barcodeData.value = value;
                        obj.set('visible', true);
                        if (DEBUG_BATCH_RENDER) {
                            console.log('[数据绑定] 条码生成成功:', componentName, '=', value);
                        }
                    }
                } else {
                    // 没有值时，隐藏该组件
                    obj.set('visible', false);
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 条码组件无数据，隐藏:', componentName);
                    }
                }
            } else if (obj.qrData) {
                // 二维码对象：只有在有值时才生成二维码图像
                if (hasValue && value.trim() !== '') {
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 开始生成二维码:', componentName, '=', value);
                        console.log('[数据绑定] qrData内容:', obj.qrData);
                        console.log('[数据绑定] qrOptions:', obj.qrData.qrOptions);
                    }
                    // 记录当前的视觉大小（宽 * 缩放）
                    const currentWidth = obj.width * obj.scaleX;
                    const currentHeight = obj.height * obj.scaleY;

                    const qrImg = await generateBarcode('qr', value, 100, obj.qrData.qrOptions);
                    await obj.setSrc(qrImg);

                    // 恢复视觉大小
                    // setSrc 会重置 width/height，但保持 scaleX/scaleY
                    // 我们需要调整 scale 以保持视觉大小不变
                    if (obj.width && obj.height) {
                        obj.scaleX = currentWidth / obj.width;
                        obj.scaleY = currentHeight / obj.height;
                    }

                    obj.qrData.value = value;
                    obj.set('visible', true);
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 二维码生成成功:', componentName, '=', value);
                    }
                } else {
                    // 没有值时，隐藏该组件
                    obj.set('visible', false);
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] 二维码组件无数据，隐藏:', componentName);
                    }
                }
            } else if (obj.type === 'image' && hasValue && value.trim() !== '') {
                // Fallback: 如果是image组件但没有barcodeData/qrData元数据
                // 尝试根据componentName推断是否为条码或二维码
                const lowerName = componentName.toLowerCase();

                if (lowerName.includes('qr') || lowerName.includes('二维码')) {
                    // 推断为二维码
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] Fallback: 推断为二维码组件，生成二维码:', componentName, '=', value);
                        console.log('[数据绑定] 推断组件无qrOptions，使用默认参数');
                    }
                    const currentWidth = obj.width * obj.scaleX;
                    const currentHeight = obj.height * obj.scaleY;

                    const defaultQrOptions = {
                        errorCorrectionLevel: 'M' as const,
                        margin: 3,
                        scale: 10,
                        color: {
                            dark: '#000000ff',
                            light: '#ffffffff'
                        }
                    };
                    const qrImg = await generateBarcode('qr', value, 100, defaultQrOptions);
                    await obj.setSrc(qrImg);

                    if (obj.width && obj.height) {
                        obj.scaleX = currentWidth / obj.width;
                        obj.scaleY = currentHeight / obj.height;
                    }

                    // 创建元数据以便后续使用
                    obj.qrData = { value };
                    obj.set('visible', true);
                } else if (lowerName.includes('barcode') || lowerName.includes('条码') || lowerName.includes('code')) {
                    // 推断为条码，默认使用CODE128
                    if (DEBUG_BATCH_RENDER) {
                        console.log('[数据绑定] Fallback: 推断为条码组件，生成条码:', componentName, '=', value);
                    }
                    const barcodeImg = await generateBarcode('qr', value, 100);
                    await obj.setSrc(barcodeImg);

                    // 创建元数据以便后续使用
                    obj.barcodeData = { type: 'qr', value, height: 100 };
                    obj.set('visible', true);
                }
            }
        }

        // 应用自动缩小字号功能
        this.applyAutoShrink(objects);
    }

    /**
     * 应用自动缩小字号功能
     *
     * 智能调整文本字体大小以适应容器限制：
     * 1. 检查文本对象的maxLines设置
     * 2. 如果实际行数超过限制，逐步缩小字体
     * 3. 保持最小字体大小限制，避免过小而影响可读性
     * 4. 只在渲染时临时调整，不修改原始模板数据
     *
     * 算法：从原始字体大小开始，每次减小1px，直到行数满足要求
     *
     * @param objects - 画布上的所有对象数组
     */
    private applyAutoShrink(objects: any[]): void {
        for (const obj of objects) {
            // 只处理文本对象
            if (obj.type !== 'textbox' && obj.type !== 'text') continue;

            // 检查是否启用了自动缩小功能
            const autoShrinkEnabled = obj.autoShrinkEnabled;
            const maxLines = obj.maxLines;

            // 如果没有启用自动缩小或没有设置最大行数，跳过
            if (!autoShrinkEnabled || !maxLines) continue;

            // 获取原始字体大小
            const originalFontSize = obj.originalFontSize || obj.fontSize;
            if (!originalFontSize) continue;

            // 获取最小字体大小
            const minFontSize = obj.minFontSize || 13; // 默认10pt转换为px (假设标准DPI缩放)

            // 保存当前字体大小，以便在不需要调整时恢复
            const currentFontSize = obj.fontSize;

            // 先重置为原始字体大小
            obj.set('fontSize', originalFontSize);
            if (obj.styles) {
                obj.styles = {}; // 清除局部样式
            }
            obj.initDimensions(); // 重新计算尺寸
            obj.setCoords();

            // 获取当前行数
            let actualLines = obj._textLines?.length || 1;

            // 如果行数未超过限制，则恢复为当前字体大小
            if (actualLines <= maxLines) {
                obj.set('fontSize', currentFontSize);
                obj.initDimensions();
                obj.setCoords();
                continue;
            }

            let adjustedFontSize = originalFontSize;

            // 循环减小字体大小，直到行数满足要求或达到最小字体大小
            while (actualLines > maxLines && adjustedFontSize > minFontSize) {
                adjustedFontSize = Math.round(adjustedFontSize - 1); // 每次减小 1pt (转换为px)
                if (adjustedFontSize < minFontSize) {
                    adjustedFontSize = minFontSize;
                }
                obj.set('fontSize', adjustedFontSize);
                if (obj.styles) {
                    obj.styles = {};
                }
                obj.initDimensions();
                obj.setCoords();
                actualLines = obj._textLines?.length || 1;
            }

            // 注意：不保存调整后的字体大小到对象本身，只在渲染时临时使用
            // 这样可以避免修改原始模板数据
        }
    }

    /**
     * 应用渲染质量控制设置
     *
     * 根据渲染选项配置 canvas 的渲染质量参数，包括性能优化和视觉质量设置
     *
     * @param options 渲染选项
     */
    private applyRenderQualitySettings(options: Required<RenderOptions>): void {
        if (!this.canvas) return;

        const renderConfig = configManager.getRenderConfig();

        // 设置图像平滑
        if (options.enableImageSmoothing !== undefined || renderConfig.enableImageSmoothing) {
            const ctx = this.canvas.getContext();
            if (ctx) {
                const smoothingEnabled = options.enableImageSmoothing ?? renderConfig.enableImageSmoothing ?? true;
                ctx.imageSmoothingEnabled = smoothingEnabled;
                // 设置图像平滑质量（如果支持）
                if ('imageSmoothingQuality' in ctx) {
                    ctx.imageSmoothingQuality = smoothingEnabled ? 'high' : 'low';
                }
            }
        }

        // 设置抗锯齿（通过 CSS 属性）
        if (options.enableAntiAliasing !== undefined || renderConfig.enableAntiAliasing) {
            const antialiasingEnabled = options.enableAntiAliasing ?? renderConfig.enableAntiAliasing ?? true;
            if (this.canvasEl) {
                this.canvasEl.style.imageRendering = antialiasingEnabled ? 'auto' : 'pixelated';
            }
        }

        // 应用文本渲染优化
        if (renderConfig.enableTextOptimization) {
            this.applyTextOptimization();
        }

        // 设置渲染超时（通过Promise.race实现）
        if (renderConfig.renderTimeout) {
            this.renderTimeout = renderConfig.renderTimeout;
        }

        // 注意：色彩深度设置主要通过输出格式和质量参数控制
        // 在 toDataURL 时会应用这些设置
    }

    /**
     * 应用文本渲染优化
     *
     * 优化文本对象的渲染质量和性能
     */
    private applyTextOptimization(): void {
        if (!this.canvas) return;

        const objects = this.canvas.getObjects();
        objects.forEach((obj: any) => {
            if (obj.type === 'textbox' || obj.type === 'text') {
                // 启用子像素渲染（如果支持）
                if (obj.set && typeof obj.set === 'function') {
                    obj.set({
                        // 优化字体渲染
                        fontSmoothing: 'antialiased',
                        // 启用字距调整
                        charSpacing: obj.charSpacing || 0,
                        // 优化行高
                        lineHeight: obj.lineHeight || 1.2
                    });
                }
            }
        });
    }

    /**
     * 应用色彩模式转换
     *
     * 将canvas转换为指定的色彩模式（彩色、灰度、黑色）
     *
     * @param canvas 原始canvas元素
     * @param colorMode 色彩模式
     * @returns 处理后的canvas元素，失败时返回原canvas
     */
    private applyColorMode(canvas: HTMLCanvasElement, colorMode: 'color' | 'grayscale' | 'blackwhite'): HTMLCanvasElement {
        // 如果不需要转换或canvas无效，直接返回原canvas
        if (colorMode === 'color' || !canvas) {
            return canvas;
        }

        try {

            // 创建新的canvas用于处理
            const processedCanvas = document.createElement('canvas');
            const processedCtx = processedCanvas.getContext('2d');

            if (!processedCtx) {
                console.warn('Failed to get 2D context for color mode processing');
                return canvas;
            }

            processedCanvas.width = canvas.width;
            processedCanvas.height = canvas.height;

            // 绘制原始canvas内容
            processedCtx.drawImage(canvas, 0, 0);

            // 获取图像数据
            const imageData = processedCtx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            if (colorMode === 'grayscale') {
                // 转换为灰度
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    // 使用标准灰度转换公式
                    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
                    data[i] = gray;     // R
                    data[i + 1] = gray; // G
                    data[i + 2] = gray; // B
                    // Alpha通道保持不变
                }
            }

            // 将处理后的数据放回canvas
            processedCtx.putImageData(imageData, 0, 0);

            return processedCanvas;
        } catch (error) {
            console.error('Error processing color mode:', error);
            // 发生错误时返回原始canvas
            return canvas;
        }
    }

    /**
     * 清理资源
     *
     * 销毁 canvas 实例并清空引用，释放内存。
     * 如果 canvas 来自外部，则不进行销毁操作。
     */
    private dispose(): void {
        if (this.canvas && !this.isExternalCanvas) {
            this.canvas.dispose();
            this.canvas = null;
        }
        if (this.canvasEl) {
            this.canvasEl = null;
        }
    }
}
